import { ChangeDetectionStrategy, Component, input, AfterViewInit, OnDestroy, ElementRef, ViewChild, Renderer2, inject } from '@angular/core';
import { ChartCardComponent } from '../../../chart-card/chart-card.component';
import { MaxTemperaturesChartData } from './max-temperatures-chart.model';
import { ChartHeader } from '@charts';
import { MaxTemperatureItemComponent } from './max-temperature-item.component';
import { TranslocoDirective } from '@jsverse/transloco';

@Component({
  selector: 'ui-max-temperatures-chart',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ChartCardComponent,
    ChartHeader,
    MaxTemperatureItemComponent,
    TranslocoDirective,
  ],
  template: `
    <ng-container *transloco="let t">
      <ui-chart-card>
        <div class="flex flex-col gap-y-4 w-full h-full">
          <chart-header
            [title]="t('CHARTS.MAX_TEMPERATURES.TITLE')"
            [subtitle]="t('CHARTS.MAX_TEMPERATURES.SUBTITLE')"
          />
          <div class="relative">
            <div #topItem class="pb-5 pt-2">
              <ui-max-temperature-item [item]="center()" [isBold]="true" />
            </div>
            <div #scrollContainer class="flex flex-col gap-y-8 overflow-y-auto flex-grow max-h-[170px]">

              <div class="flex flex-col gap-y-5 w-full h-full">
                @for (item of sortedData; track item.name) {
                  <ui-max-temperature-item [item]="item" />
                }
              </div>

              <div class="absolute top-0 left-0 w-full h-3 bg-gradient-to-b from-white to-transparent pointer-events-none"></div>
              <div class="absolute bottom-0 left-0 w-full h-3 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
            </div>
          </div>
        </div>
      </ui-chart-card>
    </ng-container>
  `,
})
export class MaxTemperaturesChart implements AfterViewInit, OnDestroy {
  center = input.required<MaxTemperaturesChartData>();
  data = input.required<MaxTemperaturesChartData[]>();

  @ViewChild('scrollContainer', { static: false }) private scrollContainerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('topItem', { static: false }) private topItemRef!: ElementRef<HTMLDivElement>;
  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private readonly renderer = inject(Renderer2);

  ngAfterViewInit(): void {
    // set initial padding based on visible scroll area
    this.updateReservedSpace();
    // keep updated on resize / layout changes
    this.resizeObserver = new ResizeObserver(() => this.updateReservedSpace());
    if (this.scrollContainerRef?.nativeElement) {
      this.resizeObserver.observe(this.scrollContainerRef.nativeElement);
    }

    // observe child changes so padding updates when items are added/removed
    if (this.scrollContainerRef?.nativeElement && typeof MutationObserver !== 'undefined') {
      this.mutationObserver = new MutationObserver(() => this.updateReservedSpace());
      this.mutationObserver.observe(this.scrollContainerRef.nativeElement, { childList: true, subtree: true });
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  private updateReservedSpace(): void {
    const scrollEl = this.scrollContainerRef?.nativeElement;
    const topEl = this.topItemRef?.nativeElement;
    if (!scrollEl || !topEl) return;

    // calcular ancho del scrollbar vertical: offsetWidth - clientWidth
    // si es 0 (scrollbars en overlay), no aplicar padding.
    const scrollbarWidth = Math.max(0, scrollEl.offsetWidth - scrollEl.clientWidth);

    if (scrollbarWidth > 0) {
      this.renderer.setStyle(topEl, 'padding-right', `${scrollbarWidth}px`);
    } else {
      this.renderer.removeStyle(topEl, 'padding-right');
    }
  }

  /**
   * Returns the data array sorted by the 'name' property.
   */
  get sortedData(): MaxTemperaturesChartData[] {
    return [...this.data()].sort((a, b) => {
      if (!a.name) return 1;
      if (!b.name) return -1;
      return a.name.localeCompare(b.name);
    });
  }
}
